import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';
import { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';

export class SectionItem implements UiItem {
    private titleRect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private childSizes: MeasureResult[] = [];
    constructor(
        private title: string,
        private children: UiItem[],
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        ctx.font = theme.fonts.section;
        const titleW = Math.min(maxW, ctx.measureText(this.title).width);
        const titleH = theme.lineHeights.section;
        let totalH = titleH;
        this.childSizes = [];
        if (this.children.length > 0) {
            totalH += theme.spacing.sectionGap;
            this.children.forEach((child, index) => {
                const size = child.measure(ctx, maxW, theme);
                this.childSizes.push(size);
                totalH += size.h;
                if (index < this.children.length - 1) totalH += theme.spacing.itemGap;
            });
        }
        return { w: titleW, h: totalH };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const titleH = theme.lineHeights.section;
        this.titleRect = { x, y, w, h: titleH };
        let cy = y + titleH + (this.children.length > 0 ? theme.spacing.sectionGap : 0);
        this.children.forEach((child, index) => {
            const size = this.childSizes[index] || { w, h: 0 };
            child.layout(x, cy, w, theme);
            cy += size.h + (index < this.children.length - 1 ? theme.spacing.itemGap : 0);
        });
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, icons?: IconRegistry): void {
        ctx.font = theme.fonts.section;
        ctx.fillStyle = theme.colors.sectionText;
        ctx.textBaseline = 'top';
        ctx.fillText(this.title, this.titleRect.x, this.titleRect.y);
        this.children.forEach((child) => child.draw(ctx, theme, icons));
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.children.forEach((child) => child.collectHitTargets(out, scrollY));
    }

    getChildren(): UiItem[] {
        return this.children;
    }
}
